import { config } from '../../../config/env';
import { REPAIR_MODEL } from './constants';

export const OPENAI_REPAIR_CONFIG = {
  API_KEY: (config.OPENAI_API_KEY || '').trim(),
  API_URL: 'https://api.openai.com/v1/chat/completions',
  MODEL: REPAIR_MODEL,
  MAX_TOKENS: 8192,
} as const;

const buildChatCompletionBody = (
  model: string,
  messages: Array<{ role: string; content: string }>,
  maxTokens: number
): Record<string, unknown> => {
  const body: Record<string, unknown> = {
    model,
    messages,
    response_format: { type: 'json_object' },
  };

  if (model.startsWith('gpt-5')) {
    body.max_completion_tokens = maxTokens;
    body.reasoning_effort = 'none';
  } else {
    body.temperature = 0.1;
    body.max_tokens = maxTokens;
  }

  return body;
};

export const callOpenAIRepair = async (
  messages: Array<{ role: string; content: string }>,
  options?: { model?: string; maxTokens?: number }
): Promise<{ content: string }> => {
  const apiKey = OPENAI_REPAIR_CONFIG.API_KEY;

  if (!apiKey) {
    throw new Error(
      'OpenAI API key is not configured. Please set VITE_OPENAI_API_KEY in your .env.local file.'
    );
  }

  const model = options?.model ?? OPENAI_REPAIR_CONFIG.MODEL;
  const response = await fetch(OPENAI_REPAIR_CONFIG.API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify(
      buildChatCompletionBody(
        model,
        messages,
        options?.maxTokens ?? OPENAI_REPAIR_CONFIG.MAX_TOKENS
      )
    ),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `OpenAI API error: ${response.status}`;

    try {
      const errorJson = JSON.parse(errorText) as {
        error?: { message?: string };
      };
      if (errorJson.error?.message) {
        errorMessage = `OpenAI API error: ${response.status} - ${errorJson.error.message}`;
      } else {
        errorMessage = `OpenAI API error: ${response.status} - ${errorText}`;
      }
    } catch {
      errorMessage = `OpenAI API error: ${response.status} - ${errorText}`;
    }

    throw new Error(errorMessage);
  }

  const data = (await response.json()) as {
    choices?: Array<{ message?: { content?: string }; finish_reason?: string }>;
  };
  const choice = data.choices?.[0];
  const content = String(choice?.message?.content ?? '').trim();

  if (!content) {
    const finishReason = choice?.finish_reason ?? 'unknown';
    throw new Error(
      `OpenAI returned empty content (finish_reason: ${finishReason}).`
    );
  }

  return { content };
};

export const isOpenAiRepairConfigured = () =>
  Boolean(OPENAI_REPAIR_CONFIG.API_KEY);
